import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Stethoscope, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function OrthodontistManager({ orthodontists, isMaster, reload }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/orthodontists", { name: name.trim() });
      toast.success("Orthodontist added");
      setName("");
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/orthodontists/${id}`);
      toast.success("Orthodontist removed");
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="manage-orthodontists-button" variant="outline" className="rounded-lg">
          <Stethoscope className="h-4 w-4 mr-1.5" /> Orthodontists
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white sm:max-w-md" data-testid="orthodontists-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">Orthodontists</DialogTitle>
          <DialogDescription>Add the specialists working at your clinic.</DialogDescription>
        </DialogHeader>
        <form onSubmit={add} className="flex items-center gap-2">
          <Input
            data-testid="orthodontist-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Dr. Meera Nair"
          />
          <Button type="submit" data-testid="orthodontist-add-submit" disabled={busy} className="bg-teal-600 hover:bg-teal-700 text-white rounded-lg shrink-0">
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </form>
        <div className="mt-2 space-y-1.5 max-h-[50vh] overflow-y-auto">
          {orthodontists.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">No orthodontists added yet.</p>
          ) : (
            orthodontists.map((o) => (
              <div
                key={o.id}
                data-testid={`orthodontist-item-${o.id}`}
                className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2"
              >
                <span className="text-sm font-medium text-slate-800 flex items-center gap-2">
                  <Stethoscope className="h-4 w-4 text-accent" /> {o.name}
                </span>
                {isMaster && (
                  <button
                    data-testid={`orthodontist-delete-${o.id}`}
                    onClick={() => remove(o.id)}
                    className="text-red-500 hover:text-red-600"
                    title="Remove"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
